/**
 * UnitRatePage.js — Estimation > Unit Rate
 *
 * Digital replica of the "LGL Fabrication Combined Hourly Rate Template"
 * Excel workbook. Saved scenarios ("sheets") live in unit_rate_sheets /
 * unit_rate_lines; every calculation from the workbook runs live here:
 *
 *   Weekly shifts  -> ST = min(shift x days, 40), OT = max(shift x (days-4), 0)
 *   Blended rate   -> (base x ST + OT rate x OT) / (ST + OT)
 *   Direct labour  -> rate component = % labour x blended
 *   Other direct   -> % labour x direct total
 *   Supervision &  -> blended x (% labour x (1 + other direct %)),
 *   support           equivalent hours = total direct hours x % labour
 *   Mat/Subs/Rent  -> Materials: % labour x (direct + other direct totals);
 *                     Subs/Rentals: their own rate (manual, X-ray calc, or
 *                     linked to an equipment cost line)
 *   Room & board   -> $/hour x % labour
 *   Summary        -> per-group markup -> selling rate -> payment terms %
 *
 * Backend: /api/unit-rate (sheets CRUD + duplicate + audit log).
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { authenticatedApiCall } from '../../../utils/apiClient';
import AuditLogModal from '../../common/AuditLogModal';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import { computeSheet, defaultSheet, uid, nz, DEFAULT_MARKUPS } from './unitRateCalc';
import { UnitRateHelpModal } from './UnitRateHelp';

/* ------------------------------------------------------------------ */
/* Constants & helpers                                                 */
/* ------------------------------------------------------------------ */

const ACCENT = '#0f766e'; // teal — distinct from Part Rate DB green
const BTN = {
  padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap',
};
const PRIMARY_BTN = { ...BTN, backgroundColor: ACCENT, color: 'white', border: `1px solid ${ACCENT}` };

const MSR_GROUPS = ['Materials', 'Subs', 'Rentals'];
const STAFF_GROUPS = ['Supervisory', 'Support Staff'];

const money = (v, dp = 2) => (v === null || v === undefined || !Number.isFinite(Number(v))
  ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const fmt = (v, dp = 2) => (v === null || v === undefined || !Number.isFinite(Number(v))
  ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const KpiCard = ({ label, value, sub, accent = ACCENT }) => (
  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem 1rem', minWidth: 160, flex: '1 1 160px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', borderTop: `3px solid ${accent}` }}>
    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: '1.45rem', fontWeight: 700, color: accent, lineHeight: 1.15, marginTop: 3 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
  </div>
);

const NumInput = ({ value, onChange, width = 84, step = 'any', placeholder = '', disabled = false }) => (
  <input
    type="number" step={step} value={value ?? ''} placeholder={placeholder} disabled={disabled}
    onChange={(e) => onChange(e.target.value === '' ? '' : e.target.value)}
    style={{ width, padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.78rem', textAlign: 'right', boxSizing: 'border-box', backgroundColor: disabled ? '#f9fafb' : 'white', color: disabled ? '#9ca3af' : '#111827' }}
  />
);

// Percentage input: stores a fraction (0.666), shows a percentage (66.6)
const PctInput = ({ value, onChange, width = 74 }) => {
  const display = value === '' || value === null || value === undefined ? '' : Math.round(Number(value) * 1e6) / 1e4;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <input
        type="number" step="any" value={display}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value) / 100)}
        style={{ width, padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.78rem', textAlign: 'right', boxSizing: 'border-box' }}
      />
      <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>%</span>
    </div>
  );
};

const TextInput = ({ value, onChange, width = 160, placeholder = '' }) => (
  <input
    type="text" value={value ?? ''} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{ width, padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.78rem', boxSizing: 'border-box' }}
  />
);

const TH = ({ children, right, width }) => (
  <th style={{ padding: '7px 8px', fontSize: '0.66rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb', textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap', width }}>{children}</th>
);
const TD = ({ children, right, bold, color }) => (
  <td style={{ padding: '5px 8px', fontSize: '0.78rem', borderBottom: '1px solid #f3f4f6', textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap', fontWeight: bold ? 700 : 400, color: color || '#111827' }}>{children}</td>
);

const SectionCard = ({ icon, title, subtitle, accent, right, children }) => (
  <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', borderBottom: '1px solid #f1f5f9', background: `linear-gradient(90deg, ${accent}14, transparent 60%)` }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1f2937' }}>{icon} {title}</div>
        {subtitle && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
    <div style={{ overflowX: 'auto' }}>{children}</div>
  </div>
);

const AddLineBtn = ({ onClick, label = 'Add line' }) => (
  <button onClick={onClick} style={{ ...BTN, padding: '4px 10px', fontSize: '0.72rem' }}>➕ {label}</button>
);
const DelBtn = ({ onClick }) => (
  <button onClick={onClick} title="Remove line" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.85rem', padding: '0 4px' }}>✕</button>
);

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

const UnitRatePage = () => {
  const { instance, accounts } = useMsal();

  const [view, setView] = useState('log'); // 'log' | 'editor'
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(true);
  const [search, setSearch] = useState('');
  const [sheet, setSheet] = useState(null); // editor state
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const toastTimer = useRef(null);
  const { viewers, isConnected, connectionState } = usePresence('unitRate', sheet?.id || 'global', true);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchSheets = useCallback(async () => {
    try {
      setLoadingSheets(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/unit-rate/sheets${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      if (r.data?.success) setSheets(r.data.data || []);
    } catch (err) {
      showToast('Failed to load rate sheets', 'error');
    } finally {
      setLoadingSheets(false);
    }
  }, [instance, accounts, search, showToast]);

  useEffect(() => { fetchSheets(); }, [fetchSheets]);

  const calc = useMemo(() => (sheet ? computeSheet(sheet) : null), [sheet]);

  /* ------------------------- sheet mutations ------------------------ */

  const patchSheet = (patch) => { setSheet((s) => ({ ...s, ...patch })); setDirty(true); };
  const patchLine = (id, patch) => {
    setSheet((s) => ({ ...s, lines: s.lines.map((l) => (l._id === id ? { ...l, ...patch } : l)) }));
    setDirty(true);
  };
  const removeLine = (id) => { setSheet((s) => ({ ...s, lines: s.lines.filter((l) => l._id !== id) })); setDirty(true); };
  const addLine = (line) => { setSheet((s) => ({ ...s, lines: [...s.lines, { _id: uid(), ...line }] })); setDirty(true); };

  const openNew = () => { setSheet(defaultSheet()); setDirty(true); setView('editor'); };

  const openSheet = async (id) => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/unit-rate/sheets/${id}`);
      if (!r.data?.success) throw new Error();
      const d = r.data.data;
      setSheet({
        ...d,
        crew_ratio: Array.isArray(d.crew_ratio) ? d.crew_ratio : [],
        markups: d.markups && typeof d.markups === 'object' ? d.markups : { ...DEFAULT_MARKUPS },
        lines: (d.lines || []).map((l) => ({ ...l, _id: uid() })),
      });
      setDirty(false);
      setView('editor');
    } catch {
      showToast('Failed to open sheet', 'error');
    }
  };

  const saveSheet = async () => {
    if (!sheet) return;
    if (!String(sheet.name || '').trim()) { showToast('Give the sheet a name first', 'error'); return; }
    setSaving(true);
    try {
      const body = {
        ...sheet,
        total_cost_rate: calc ? Math.round(calc.totalCost * 1e4) / 1e4 : null,
        total_selling_rate: calc ? Math.round(calc.finalRate * 1e4) / 1e4 : null,
        lines: sheet.lines.map((l, i) => ({ ...l, sort_order: i })),
      };
      const r = sheet.id
        ? await authenticatedApiCall(instance, accounts, 'PUT', `/unit-rate/sheets/${sheet.id}`, body)
        : await authenticatedApiCall(instance, accounts, 'POST', '/unit-rate/sheets', body);
      if (r.data?.success) {
        if (!sheet.id) setSheet((s) => ({ ...s, id: r.data.data.id }));
        setDirty(false);
        showToast('Sheet saved');
        fetchSheets();
      } else {
        showToast(r.data?.message || 'Save failed', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const duplicateSheet = async (id, name) => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `/unit-rate/sheets/${id}/duplicate`, { name: `${name} (copy)` });
      if (r.data?.success) { showToast('Sheet duplicated'); fetchSheets(); }
    } catch { showToast('Duplicate failed', 'error'); }
  };

  const deleteSheet = async (id) => {
    if (!window.confirm('Delete this rate sheet? This cannot be undone.')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/unit-rate/sheets/${id}`);
      showToast('Sheet deleted');
      if (sheet?.id === id) { setSheet(null); setView('log'); }
      fetchSheets();
    } catch { showToast('Delete failed', 'error'); }
  };

  const backToLog = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    setSheet(null); setDirty(false); setView('log'); fetchSheets();
  };

  /* --------------------------- Excel export ------------------------- */

  const exportXlsx = () => {
    if (!sheet || !calc) return;
    const rows = [];
    const push = (r) => rows.push(r);
    push([sheet.name]);
    push([`Client: ${sheet.client || '—'}`, '', `Reference: ${sheet.reference || '—'}`]);
    push([]);
    push(['Weekly Shifts', 'Working Shift', 'Working Days', 'ST Hours', 'OT Hours']);
    push(['Crew', nz(sheet.crew_shift_hours), nz(sheet.crew_shift_days), calc.crewST, calc.crewOT]);
    push(['Supervision & Support', nz(sheet.supv_shift_hours), nz(sheet.supv_shift_days), calc.supvST, calc.supvOT]);
    push(['Total Direct Labour Hours', nz(sheet.total_direct_labour_hours)]);
    push([]);
    push(['DIRECT LABOUR', 'Description', 'Base Rate', 'OT Rate', 'Blended Rate', '% Labour', 'Rate Component']);
    calc.direct.forEach((l) => push([l.group_name, l.description, nz(l.base_rate), nz(l.ot_rate), l.blended, nz(l.pct_labour), l.rc]));
    push(['Total', '', '', '', '', '', calc.directTotal]);
    push([]);
    push(['OTHER DIRECT', 'Description', '% Labour', 'Rate Component', 'Notes']);
    calc.otherDirect.forEach((l) => push([l.group_name, l.description, nz(l.pct_labour), l.rc, l.notes || '']));
    push(['Total', '', '', calc.otherDirectTotal]);
    push([]);
    push(['SUPERVISION & SUPPORT', 'Description', 'Base Rate', 'OT Rate', 'Blended Rate', '% Labour', 'Other Direct %', 'Rate Component', 'Equivalent Hours']);
    calc.staff.forEach((l) => push([l.group_name, l.description, nz(l.base_rate), nz(l.ot_rate), l.blended, nz(l.pct_labour), nz(l.other_direct_pct), l.rc, l.equivHours]));
    push(['Total', '', '', '', '', '', '', calc.staffTotal, calc.staffEquivHours]);
    push([]);
    push(['MATERIALS / SUBS / RENTALS', 'Description', 'Rate', '% Labour', 'Rate Component', 'Notes']);
    calc.msr.forEach((l) => push([l.group_name, l.description, l.rate, nz(l.pct_labour), l.rc, l.notes || '']));
    push(['Total', '', '', '', calc.msrTotal]);
    push([]);
    push(['ROOM & BOARD', 'Description', 'Rate ($/hr)', '% Labour', 'Rate Component', 'Notes']);
    calc.roomBoard.forEach((l) => push([l.group_name, l.description, nz(l.rate_per_hour), nz(l.pct_labour), l.rc, l.notes || '']));
    push(['Total', '', '', '', calc.roomBoardTotal]);
    push([]);
    push(['SUMMARY', 'Rate Cost', 'M/U', 'Selling Rate']);
    calc.summary.forEach((g) => push([g.label, g.cost, g.mu, g.selling]));
    push(['Total', calc.totalCost, '', calc.totalSelling]);
    push(['Payment Terms %', nz(sheet.payment_terms_pct)]);
    push(['FINAL SELLING RATE', '', '', calc.finalRate]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 26 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 13 }, { wch: 11 }, { wch: 13 }, { wch: 14 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unit Rate');
    XLSX.writeFile(wb, `${(sheet.name || 'unit_rate').replace(/[^\w\- ]+/g, '')}.xlsx`);
    showToast('Exported to Excel');
  };

  /* ------------------------------ log view -------------------------- */

  const logKpis = useMemo(() => {
    const withRate = sheets.filter((s) => s.total_selling_rate !== null && s.total_selling_rate !== undefined);
    const avg = withRate.length ? withRate.reduce((s, x) => s + Number(x.total_selling_rate), 0) / withRate.length : null;
    return {
      count: sheets.length,
      avgSelling: avg,
      lastUpdated: sheets.length ? new Date(Math.max(...sheets.map((s) => new Date(s.updated_at).getTime()))) : null,
    };
  }, [sheets]);

  const renderLog = () => (
    <div style={{ padding: 24 }}>
      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 100%)`, borderRadius: 14, padding: '1.3rem 1.5rem', color: 'white', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem' }}>🧮 Unit Rate</h2>
          <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
            Combined hourly rate calculator — build blended crew rates, indirects, markups and a selling rate per scenario.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <button onClick={() => setShowHelp(true)} style={{ ...BTN, background: 'rgba(255,255,255,0.14)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }}>❓ Help</button>
          <button onClick={() => setShowAudit(true)} style={{ ...BTN, background: 'rgba(255,255,255,0.14)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }}>📜 Audit Log</button>
          <button onClick={openNew} style={{ ...BTN, background: 'white', color: ACCENT, fontWeight: 700 }}>➕ New Rate Sheet</button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <KpiCard label="Rate Sheets" value={logKpis.count} accent={ACCENT} />
        <KpiCard label="Avg Selling Rate" value={logKpis.avgSelling === null ? '—' : money(logKpis.avgSelling)} sub="across saved sheets" accent="#6366f1" />
        <KpiCard label="Last Updated" value={logKpis.lastUpdated ? logKpis.lastUpdated.toLocaleDateString() : '—'} accent="#f59e0b" />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <input
          type="text" placeholder="Search by name, client, reference..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #dee2e6', fontSize: '0.9rem', width: 320, outline: 'none' }}
        />
      </div>

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              <TH>Name</TH><TH>Client</TH><TH>Reference</TH>
              <TH right>Cost Rate</TH><TH right>Selling Rate</TH><TH right>Lines</TH>
              <TH>Updated</TH><TH>By</TH><TH> </TH>
            </tr>
          </thead>
          <tbody>
            {loadingSheets ? (
              <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Loading…</td></tr>
            ) : sheets.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#9ca3af' }}>
                No rate sheets yet — click <strong>➕ New Rate Sheet</strong> to start from the LGL fabrication template.
              </td></tr>
            ) : sheets.map((s) => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSheet(s.id)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0fdfa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}>
                <TD bold>{s.name}</TD>
                <TD>{s.client || '—'}</TD>
                <TD>{s.reference || '—'}</TD>
                <TD right>{s.total_cost_rate === null ? '—' : money(s.total_cost_rate)}</TD>
                <TD right bold color={ACCENT}>{s.total_selling_rate === null ? '—' : money(s.total_selling_rate)}</TD>
                <TD right>{s.line_count}</TD>
                <TD>{new Date(s.updated_at).toLocaleDateString()}</TD>
                <TD>{(s.updated_by || s.created_by || '—').split('@')[0]}</TD>
                <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => duplicateSheet(s.id, s.name)} title="Duplicate" style={{ ...BTN, padding: '3px 8px', fontSize: '0.72rem', marginRight: 4 }}>⧉</button>
                  <button onClick={() => deleteSheet(s.id)} title="Delete" style={{ ...BTN, padding: '3px 8px', fontSize: '0.72rem', color: '#dc2626' }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ----------------------------- editor view ------------------------ */

  const renderEditor = () => {
    const c = calc;
    const donutData = c.summary.filter((g) => g.selling > 0).map((g) => ({ name: g.label, value: Math.round(g.selling * 100) / 100, color: g.color }));
    const barData = c.summary.map((g) => ({ name: g.label, Cost: Math.round(g.cost * 100) / 100, Selling: Math.round(g.selling * 100) / 100 }));

    return (
      <div style={{ padding: 24, background: '#f8fafc', minHeight: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={backToLog} style={BTN}>← All Sheets</button>
            <TextInput value={sheet.name} onChange={(v) => patchSheet({ name: v })} width={280} placeholder="Sheet name" />
            <TextInput value={sheet.client} onChange={(v) => patchSheet({ client: v })} width={170} placeholder="Client" />
            <TextInput value={sheet.reference} onChange={(v) => patchSheet({ reference: v })} width={170} placeholder="Reference / job #" />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={4}
              showConnectionStatus={true}
            />
            <button onClick={() => setShowHelp(true)} style={BTN}>❓ Help</button>
            <button onClick={exportXlsx} style={BTN}>📤 Export</button>
            {sheet.id && <button onClick={() => duplicateSheet(sheet.id, sheet.name)} style={BTN}>⧉ Duplicate</button>}
            {sheet.id && <button onClick={() => deleteSheet(sheet.id)} style={{ ...BTN, color: '#dc2626' }}>🗑 Delete</button>}
            <button onClick={saveSheet} disabled={saving} style={{ ...PRIMARY_BTN, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : dirty ? '💾 Save*' : '💾 Save'}
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <KpiCard label="Total Cost Rate" value={money(c.totalCost)} sub="per direct labour hour" accent="#0ea5e9" />
          <KpiCard label="Selling Rate" value={money(c.totalSelling)} sub="after markups" accent={ACCENT} />
          <KpiCard label="Final Rate" value={money(c.finalRate)} sub={`incl. payment terms ${fmt(nz(sheet.payment_terms_pct) * 100, 1)}%`} accent="#7c3aed" />
          <KpiCard label="Crew ST / OT" value={`${fmt(c.crewST, 0)} / ${fmt(c.crewOT, 0)}`} sub="hours per week" accent="#f59e0b" />
          <KpiCard label="Supv ST / OT" value={`${fmt(c.supvST, 0)} / ${fmt(c.supvOT, 0)}`} sub="hours per week" accent="#ec4899" />
          <KpiCard label="Staff Equiv. Hours" value={fmt(c.staffEquivHours, 0)} sub={`of ${fmt(nz(sheet.total_direct_labour_hours), 0)} direct hrs`} accent="#64748b" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>

          {/* Inputs: shifts + crew ratio + x-ray */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            <SectionCard icon="⏱️" title="Weekly Shifts" accent="#f59e0b" subtitle="ST = min(shift × days, 40) · OT = max(shift × (days − 4), 0)">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><TH>Crew Type</TH><TH right>Shift (hrs)</TH><TH right>Days</TH><TH right>ST</TH><TH right>OT</TH></tr></thead>
                <tbody>
                  <tr>
                    <TD>Crew</TD>
                    <TD right><NumInput value={sheet.crew_shift_hours} onChange={(v) => patchSheet({ crew_shift_hours: v })} width={70} /></TD>
                    <TD right><NumInput value={sheet.crew_shift_days} onChange={(v) => patchSheet({ crew_shift_days: v })} width={60} /></TD>
                    <TD right bold>{fmt(c.crewST, 0)}</TD><TD right bold>{fmt(c.crewOT, 0)}</TD>
                  </tr>
                  <tr>
                    <TD>Supervision &amp; Support</TD>
                    <TD right><NumInput value={sheet.supv_shift_hours} onChange={(v) => patchSheet({ supv_shift_hours: v })} width={70} /></TD>
                    <TD right><NumInput value={sheet.supv_shift_days} onChange={(v) => patchSheet({ supv_shift_days: v })} width={60} /></TD>
                    <TD right bold>{fmt(c.supvST, 0)}</TD><TD right bold>{fmt(c.supvOT, 0)}</TD>
                  </tr>
                  <tr>
                    <TD bold>Total Direct Labour Hours</TD>
                    <TD right><NumInput value={sheet.total_direct_labour_hours} onChange={(v) => patchSheet({ total_direct_labour_hours: v })} width={100} /></TD>
                    <TD /><TD /><TD />
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard icon="👥" title="Crew Ratio" accent="#8b5cf6" subtitle="Informational — documents the crew mix behind % labour"
              right={<AddLineBtn label="Add" onClick={() => patchSheet({ crew_ratio: [...(sheet.crew_ratio || []), { occupation: '', ratio: '' }] })} />}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><TH>Occupation</TH><TH right>Ratio</TH><TH width={30} /></tr></thead>
                <tbody>
                  {(sheet.crew_ratio || []).map((r, i) => (
                    <tr key={i}>
                      <TD><TextInput value={r.occupation} width={140} onChange={(v) => {
                        const next = [...sheet.crew_ratio]; next[i] = { ...next[i], occupation: v }; patchSheet({ crew_ratio: next });
                      }} /></TD>
                      <TD right><NumInput value={r.ratio} width={64} onChange={(v) => {
                        const next = [...sheet.crew_ratio]; next[i] = { ...next[i], ratio: v }; patchSheet({ crew_ratio: next });
                      }} /></TD>
                      <TD right><DelBtn onClick={() => patchSheet({ crew_ratio: sheet.crew_ratio.filter((_, j) => j !== i) })} /></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard icon="☢️" title="X-ray Calculation" accent="#10b981" subtitle="Feeds Subs lines linked to X-ray · Cost/Hour = Total Cost ÷ Total Hours">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><TD>Total Weld Count</TD><TD right><NumInput value={sheet.xray_weld_count} onChange={(v) => patchSheet({ xray_weld_count: v })} /></TD></tr>
                  <tr><TD>X-ray Requirement</TD><TD right><PctInput value={sheet.xray_requirement_pct} onChange={(v) => patchSheet({ xray_requirement_pct: v })} /></TD></tr>
                  <tr><TD>Cost / X-ray</TD><TD right><NumInput value={sheet.xray_cost_each} onChange={(v) => patchSheet({ xray_cost_each: v })} /></TD></tr>
                  <tr>
                    <TD>Total Cost <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>(suggested {money(nz(sheet.xray_weld_count) * nz(sheet.xray_requirement_pct) * nz(sheet.xray_cost_each), 0)})</span></TD>
                    <TD right><NumInput value={sheet.xray_total_cost} onChange={(v) => patchSheet({ xray_total_cost: v })} width={100} /></TD>
                  </tr>
                  <tr><TD>Total Hours</TD><TD right><NumInput value={sheet.xray_total_hours} onChange={(v) => patchSheet({ xray_total_hours: v })} width={100} /></TD></tr>
                  <tr><TD bold>Cost / Hour</TD><TD right bold color="#10b981">{money(c.xrayCostPerHour)}</TD></tr>
                </tbody>
              </table>
            </SectionCard>
          </div>

          {/* 1. Direct Labour */}
          <SectionCard icon="🔧" title="1 · Direct Labour" accent="#0ea5e9"
            subtitle="Blended Rate = (Base × ST + OT Rate × OT) ÷ (ST + OT) · Rate Component = % Labour × Blended"
            right={<AddLineBtn onClick={() => addLine({ section: 'direct', group_name: 'Direct Labour', description: '', base_rate: '', ot_rate: '', pct_labour: '' })} />}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
              <thead><tr>
                <TH>Description</TH><TH right>Base Rate</TH><TH right>OT Rate</TH>
                <TH right>ST</TH><TH right>OT</TH><TH right>Blended</TH><TH right>% Labour</TH><TH right>Rate Component</TH><TH width={30} />
              </tr></thead>
              <tbody>
                {c.direct.map((l) => (
                  <tr key={l._id}>
                    <TD><TextInput value={l.description} width={190} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                    <TD right><NumInput value={l.base_rate} onChange={(v) => patchLine(l._id, { base_rate: v })} /></TD>
                    <TD right><NumInput value={l.ot_rate} onChange={(v) => patchLine(l._id, { ot_rate: v })} /></TD>
                    <TD right color="#9ca3af">{fmt(l.st, 0)}</TD>
                    <TD right color="#9ca3af">{fmt(l.ot, 0)}</TD>
                    <TD right>{money(l.blended)}</TD>
                    <TD right><PctInput value={l.pct_labour} onChange={(v) => patchLine(l._id, { pct_labour: v })} /></TD>
                    <TD right bold color="#0ea5e9">{money(l.rc)}</TD>
                    <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                  </tr>
                ))}
                <tr style={{ background: '#f0f9ff' }}>
                  <TD bold>Total Direct</TD><TD /><TD /><TD /><TD /><TD />
                  <TD right color="#64748b">{fmt(c.direct.reduce((s, l) => s + nz(l.pct_labour), 0) * 100, 1)}%</TD>
                  <TD right bold color="#0ea5e9">{money(c.directTotal)}</TD><TD />
                </tr>
              </tbody>
            </table>
          </SectionCard>

          {/* 2. Other Direct */}
          <SectionCard icon="📦" title="2 · Other Direct" accent="#6366f1"
            subtitle="Rate Component = % Labour × Total Direct rate component"
            right={<AddLineBtn onClick={() => addLine({ section: 'other_direct', group_name: 'Other Direct', description: '', pct_labour: '' })} />}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr><TH>Description</TH><TH right>% Labour</TH><TH right>Rate Component</TH><TH>Notes</TH><TH width={30} /></tr></thead>
              <tbody>
                {c.otherDirect.map((l) => (
                  <tr key={l._id}>
                    <TD><TextInput value={l.description} width={200} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                    <TD right><PctInput value={l.pct_labour} onChange={(v) => patchLine(l._id, { pct_labour: v })} /></TD>
                    <TD right bold color="#6366f1">{money(l.rc)}</TD>
                    <TD><TextInput value={l.notes} width={230} onChange={(v) => patchLine(l._id, { notes: v })} placeholder="—" /></TD>
                    <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                  </tr>
                ))}
                <tr style={{ background: '#eef2ff' }}>
                  <TD bold>Total Other Direct</TD>
                  <TD right color="#64748b">{fmt(c.otherDirect.reduce((s, l) => s + nz(l.pct_labour), 0) * 100, 1)}%</TD>
                  <TD right bold color="#6366f1">{money(c.otherDirectTotal)}</TD><TD /><TD />
                </tr>
              </tbody>
            </table>
          </SectionCard>

          {/* 3. Supervision & Support */}
          <SectionCard icon="👷" title="3 · Supervision & Support Staff" accent="#ec4899"
            subtitle="Rate Component = Blended × (% Labour × (1 + Other Direct %)) · Equivalent Hours = Total Direct Hours × % Labour"
            right={<AddLineBtn onClick={() => addLine({ section: 'staff', group_name: 'Support Staff', description: '', base_rate: '', ot_rate: '', pct_labour: '', other_direct_pct: 0.06 })} />}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
              <thead><tr>
                <TH>Group</TH><TH>Description</TH><TH right>Base Rate</TH><TH right>OT Rate</TH>
                <TH right>Blended</TH><TH right>% Labour</TH><TH right>Other Dir %</TH><TH right>Rate Component</TH><TH right>Equiv. Hours</TH><TH width={30} />
              </tr></thead>
              <tbody>
                {c.staff.map((l) => (
                  <tr key={l._id}>
                    <TD>
                      <select value={l.group_name || 'Support Staff'} onChange={(e) => patchLine(l._id, { group_name: e.target.value })}
                        style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.76rem' }}>
                        {STAFF_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </TD>
                    <TD><TextInput value={l.description} width={170} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                    <TD right><NumInput value={l.base_rate} onChange={(v) => patchLine(l._id, { base_rate: v })} /></TD>
                    <TD right><NumInput value={l.ot_rate} onChange={(v) => patchLine(l._id, { ot_rate: v })} /></TD>
                    <TD right>{money(l.blended)}</TD>
                    <TD right><PctInput value={l.pct_labour} onChange={(v) => patchLine(l._id, { pct_labour: v })} /></TD>
                    <TD right><PctInput value={l.other_direct_pct} width={60} onChange={(v) => patchLine(l._id, { other_direct_pct: v })} /></TD>
                    <TD right bold color="#ec4899">{money(l.rc)}</TD>
                    <TD right color="#64748b">{fmt(l.equivHours, 0)}</TD>
                    <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                  </tr>
                ))}
                <tr style={{ background: '#fdf2f8' }}>
                  <TD bold>Total</TD><TD color="#64748b">Supervisory {money(c.supervisoryTotal)} · Support {money(c.supportStaffTotal)}</TD>
                  <TD /><TD /><TD /><TD /><TD />
                  <TD right bold color="#ec4899">{money(c.staffTotal)}</TD>
                  <TD right bold color="#64748b">{fmt(c.staffEquivHours, 0)}</TD><TD />
                </tr>
              </tbody>
            </table>
          </SectionCard>

          {/* 4. Materials / Subs / Rentals + Equipment */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
            <SectionCard icon="🚚" title="4 · Materials / Subs / Rentals" accent="#f59e0b"
              subtitle="Materials: % Labour × (Direct + Other Direct) · Subs/Rentals: own rate (manual, X-ray or equipment link)"
              right={<AddLineBtn onClick={() => addLine({ section: 'msr', group_name: 'Subs', description: '', blended_rate: '', link_type: 'none' })} />}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead><tr>
                  <TH>Group</TH><TH>Description</TH><TH>Source</TH><TH right>Rate</TH><TH right>% Labour</TH><TH right>Rate Component</TH><TH width={30} />
                </tr></thead>
                <tbody>
                  {c.msr.map((l) => (
                    <tr key={l._id}>
                      <TD>
                        <select value={l.group_name || 'Subs'} onChange={(e) => patchLine(l._id, { group_name: e.target.value })}
                          style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.76rem' }}>
                          {MSR_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </TD>
                      <TD><TextInput value={l.description} width={150} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                      <TD>
                        {l.group_name === 'Materials' ? (
                          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>% of labour</span>
                        ) : (
                          <select value={l.link_type || 'none'} onChange={(e) => patchLine(l._id, { link_type: e.target.value })}
                            style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.76rem' }}>
                            <option value="none">Manual</option>
                            <option value="xray">X-ray calc</option>
                            <option value="equipment">Equipment</option>
                          </select>
                        )}
                        {l.link_type === 'equipment' && l.group_name !== 'Materials' && (
                          <select value={l.linked_description || ''} onChange={(e) => patchLine(l._id, { linked_description: e.target.value })}
                            style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.76rem', marginLeft: 4 }}>
                            <option value="">— pick —</option>
                            {c.equipment.map((e2) => <option key={e2._id} value={e2.description}>{e2.description}</option>)}
                          </select>
                        )}
                      </TD>
                      <TD right>
                        {l.group_name === 'Materials' ? <span style={{ color: '#9ca3af' }}>—</span>
                          : l.link_type === 'none'
                            ? <NumInput value={l.blended_rate} onChange={(v) => patchLine(l._id, { blended_rate: v })} />
                            : <span>{money(l.rate)}</span>}
                      </TD>
                      <TD right>
                        {l.group_name === 'Materials'
                          ? <PctInput value={l.pct_labour} onChange={(v) => patchLine(l._id, { pct_labour: v })} />
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </TD>
                      <TD right bold color="#f59e0b">{money(l.rc)}</TD>
                      <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                    </tr>
                  ))}
                  <tr style={{ background: '#fffbeb' }}>
                    <TD bold>Total</TD><TD /><TD /><TD /><TD />
                    <TD right bold color="#f59e0b">{money(c.msrTotal)}</TD><TD />
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard icon="🚜" title="Equipment Cost" accent="#14b8a6"
              subtitle="Cost = Weekly Rate × % Use ÷ Working Week Hours — link Rentals lines to these"
              right={<AddLineBtn onClick={() => addLine({ section: 'equipment', group_name: 'Equipment', description: '', weekly_rate: '', pct_use: 0, week_hours: 40 })} />}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                <thead><tr>
                  <TH>Equipment</TH><TH right>Weekly Rate</TH><TH right>% Use</TH><TH right>Week Hours</TH><TH right>Cost / Hr</TH><TH width={30} />
                </tr></thead>
                <tbody>
                  {c.equipment.map((l) => (
                    <tr key={l._id}>
                      <TD><TextInput value={l.description} width={130} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                      <TD right><NumInput value={l.weekly_rate} onChange={(v) => patchLine(l._id, { weekly_rate: v })} /></TD>
                      <TD right><PctInput value={l.pct_use} onChange={(v) => patchLine(l._id, { pct_use: v })} /></TD>
                      <TD right><NumInput value={l.week_hours} width={70} onChange={(v) => patchLine(l._id, { week_hours: v })} /></TD>
                      <TD right bold color="#14b8a6">{money(l.cost)}</TD>
                      <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          </div>

          {/* 5. Room & Board */}
          <SectionCard icon="🏨" title="5 · Room & Board" accent="#64748b"
            subtitle="Rate Component = Rate ($/hour) × % Labour"
            right={<AddLineBtn onClick={() => addLine({ section: 'room_board', group_name: 'Room and Board', description: '', rate_per_hour: '', pct_labour: '' })} />}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead><tr><TH>Description</TH><TH right>Rate ($/hr)</TH><TH right>% Labour</TH><TH right>Rate Component</TH><TH>Notes</TH><TH width={30} /></tr></thead>
              <tbody>
                {c.roomBoard.map((l) => (
                  <tr key={l._id}>
                    <TD><TextInput value={l.description} width={200} onChange={(v) => patchLine(l._id, { description: v })} /></TD>
                    <TD right><NumInput value={l.rate_per_hour} onChange={(v) => patchLine(l._id, { rate_per_hour: v })} /></TD>
                    <TD right><PctInput value={l.pct_labour} onChange={(v) => patchLine(l._id, { pct_labour: v })} /></TD>
                    <TD right bold color="#64748b">{money(l.rc)}</TD>
                    <TD><TextInput value={l.notes} width={230} onChange={(v) => patchLine(l._id, { notes: v })} placeholder="—" /></TD>
                    <TD right><DelBtn onClick={() => removeLine(l._id)} /></TD>
                  </tr>
                ))}
                <tr style={{ background: '#f8fafc' }}>
                  <TD bold>Total Room &amp; Board</TD><TD /><TD />
                  <TD right bold color="#64748b">{money(c.roomBoardTotal)}</TD><TD /><TD />
                </tr>
              </tbody>
            </table>
          </SectionCard>

          {/* Summary + charts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16 }}>
            <SectionCard icon="🧾" title="Summary & Markups" accent={ACCENT} subtitle="Selling Rate = Rate Cost × (1 + M/U) · Final = Total × (1 + Payment Terms %)">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><TH>Group</TH><TH right>Rate Cost</TH><TH right>M/U</TH><TH right>Selling Rate</TH></tr></thead>
                <tbody>
                  {c.summary.map((g) => (
                    <tr key={g.key}>
                      <TD><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: g.color, marginRight: 7 }} />{g.label}</TD>
                      <TD right>{money(g.cost)}</TD>
                      <TD right><PctInput value={c.markups[g.key]} width={62} onChange={(v) => patchSheet({ markups: { ...c.markups, [g.key]: v === '' ? 0 : v } })} /></TD>
                      <TD right bold color={ACCENT}>{money(g.selling)}</TD>
                    </tr>
                  ))}
                  <tr style={{ background: '#f0fdfa' }}>
                    <TD bold>Total</TD>
                    <TD right bold>{money(c.totalCost)}</TD><TD />
                    <TD right bold color={ACCENT}>{money(c.totalSelling)}</TD>
                  </tr>
                  <tr>
                    <TD bold>Payment Treatment Time</TD>
                    <TD /><TD right><PctInput value={sheet.payment_terms_pct} width={62} onChange={(v) => patchSheet({ payment_terms_pct: v === '' ? 0 : v })} /></TD>
                    <TD right bold color="#7c3aed" >{money(c.finalRate)}</TD>
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard icon="📊" title="Selling Rate Composition" accent="#7c3aed">
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ width: 260, height: 230 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                        {donutData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <ReTooltip formatter={(v) => money(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ flex: '1 1 200px', minWidth: 200, height: 230, padding: '8px 10px 8px 0' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-28} textAnchor="end" height={52} />
                      <YAxis tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `$${v}`} />
                      <ReTooltip formatter={(v) => money(v)} />
                      <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                      <Bar dataKey="Cost" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Selling" fill={ACCENT} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Notes */}
          <SectionCard icon="📝" title="Notes" accent="#94a3b8">
            <textarea
              value={sheet.notes ?? ''} onChange={(e) => patchSheet({ notes: e.target.value })}
              placeholder="Assumptions, exclusions, context for this rate…"
              style={{ width: '100%', minHeight: 70, border: 'none', outline: 'none', padding: '10px 14px', fontSize: '0.82rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </SectionCard>
        </div>
      </div>
    );
  };

  /* ------------------------------ render ---------------------------- */

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 10001, padding: '12px 20px', borderRadius: 8, color: 'white', fontWeight: 500, backgroundColor: toast.type === 'error' ? '#dc3545' : ACCENT, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      {view === 'log' || !sheet ? renderLog() : renderEditor()}

      <AuditLogModal isOpen={showAudit} onClose={() => setShowAudit(false)} endpoint="/unit-rate/audit-log" title="Unit Rate Audit Log" accentColor={ACCENT} />

      <UnitRateHelpModal open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default UnitRatePage;
